<template>
  <v-container fluid>
    <h2>
      {{ $tt('observation.soilMoistureList') }}
    </h2>

    <v-btn class="ma-3" small color="warning" @click="openEditor()" v-if="canCreateNew()">
      <v-icon>mdi-plus</v-icon>
      {{ newButtonDescription }}
    </v-btn>

    <v-row class="white pa-2 mb-2" no-gutters v-for="(oInfo, i) in pfObservationInfos" :key="i" v-resize="onResize">
      <!-- 圃場表記 -->
      <v-col class=" pt-2 pl-2" :class="{ 'col-4': !isMobile, 'col-12': isMobile }">
        <v-layout justify-start align-center>
          <!-- 圃場サムネイル -->
          <v-img max-width="100" :src="thumbnailUrl(oInfo.field)" />
          <v-sheet class="pl-4">
            <div>{{ oInfo.field.name }}</div>
            <div class="subtitle-2" v-if="!oInfo.field.deleted">
              &#9670;
              {{ $tt('observation.drainage') }}
              =
              {{ getSoilDescription(oInfo.observation.soil) }}
            </div>
            <v-row v-for="(planting, j) in listCurrentPlantings(oInfo.field)" :key="j" class="mt-0 pt-0">
              <v-col cols="12" class="my-0 py-0">
                <planting-indicator-component :planting="planting" small :display-year="false" />
              </v-col>
            </v-row>
          </v-sheet>
        </v-layout>
      </v-col>

      <!-- PF表記 -->
      <v-col class="pl-2 pt-2" :class="{ 'col-6': !isMobile, 'col-10': isMobile }" v-if="!oInfo.field.deleted">
        <v-layout justify-start align-center>
          <v-layout justify-start align-center v-if="oInfo.weatherModelFromOpenMeteo && isOpenMeteoEnabled">
            <v-avatar class="mt-4" width="70" height="70">
              <v-img :src="oInfo.weatherModelFromOpenMeteo.soilMoisture.icon" />
            </v-avatar>
            <v-sheet class="pl-4 mt-5">
              <h3 :style="oInfo.weatherModelFromOpenMeteo.soilMoisture.fontColorStyle">
                {{ oInfo.weatherModelFromOpenMeteo.soilMoisture.description }}
              </h3>
              <small>
                {{ oInfo.weatherModelFromOpenMeteo.date }}
              </small>
            </v-sheet>
          </v-layout>
        </v-layout>
      </v-col>

      <!-- ボタン -->
      <v-col class="pr-2" :class="{ 'col-2': !isMobile, 'col-2': isMobile }">
        <v-layout class="mt-10" justify-end align-baseline>
          <v-btn icon @click="openEditor(oInfo.observation)" v-if="!oInfo.field.deleted">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="openDeleteDialog(oInfo)">
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </v-layout>
      </v-col>

      <!-- グラフ -->
      <v-col class="col-12 mt-4" v-if="oInfo.weatherModelFromOpenMeteo && isOpenMeteoEnabled">
        <chart-component :observed-data="oInfo.weatherModelFromOpenMeteo.observedList" />
      </v-col>
    </v-row>

    <!--
    編集ダイアログ
    -->
    <v-dialog v-model="showingEditor" max-width="80%" persistent>
      <v-card>
        <v-card-title>
          {{ $tt('observation.registerAndEditSoilMoisturePoints') }}
        </v-card-title>
        <v-card-text>
          <v-card outlined>
            <my-field-select-component
              :multiple="false"
              v-model="selectedFieldId"
              :error="fieldSelectError"
              :error-message="fieldSelectErrorMessage"
              field-value="id"
            />
          </v-card>
          <v-card outlined class="mt-3">
            <v-card-title>
              <h6>{{ $tt('observation.drainage') }}</h6>
            </v-card-title>
            <v-card-text>
              <v-radio-group row v-model="selectedSoil">
                <v-radio :label="$tt('observation.soilMoistureConditionSandyLoam')" :value="soil.SANDY_LOAM" />
                <v-radio :label="$tt('observation.soilMoistureConditionClayLoam')" :value="soil.CLAY_LOAM" />
                <v-radio :label="$tt('observation.soilMoistureConditionClay')" :value="soil.CLAY" />
              </v-radio-group>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeEditor()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click.stop="createOrUpdate()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
    削除ダイアログ
    -->
    <v-dialog v-model="showingDeleteDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ deleteConfirmationMessage }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeDeleteDialog">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="destroy()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ObservationListMixin from '@/components/mixins/observation-list-mixin'
import MyFieldSelectComponent from '@/components/MyFieldSelectComponent'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import * as Constants from '@/lib/constants'
import { Soil } from '@/lib/soil'
import i18n from '@/lib/i18n'
import * as ObservationConstants from '@/lib/observation-constants'
import ObservationInfo from '@/lib/observation-info'
import UserInfo from '@/lib/user-info'
import ChartComponent from '@/components/observations/SoilMoistureChart.vue'
import ApplicationConfig from '@/lib/application-config'

export default {
  name: 'SoilMoistureListAsList',
  components: { MyFieldSelectComponent, PlantingIndicatorComponent, ChartComponent },
  mixins: [ObservationListMixin],
  data() {
    return {
      showingEditor: false,

      selectedFieldId: null,
      selectedSoil: Soil.SANDY_LOAM,
      selectedObservation: null,

      showingDeleteDialog: false,
      deleteConfirmationMessage: null,

      fieldSelectError: false,
      fieldSelectErrorMessage: null,

      isMobile: false,
      isOpenMeteoEnabled: ApplicationConfig.OPEN_METEO_ENABLED,
    }
  },
  computed: {
    pfObservationInfos() {
      const observations = this.$store.getters.observations.pf
      let list = []
      for (const observation of observations) {
        const oInfo = new ObservationInfo(observation, ObservationConstants.TYPE_SOLID_MOISTURE)
        list.push(oInfo)
      }
      return list
    },
    soil() {
      return Soil
    },
    maxObservationCount() {
      const f = UserInfo.getFunctionality()
      return Constants.MaxObservationCount.PF[f]
    },
    newButtonDescription() {
      const count = this.maxObservationCount
      return i18n.tt('customNewRegistration') + i18n.tt('observation.maxRegisterCount', { count })
    },
    growSoilMoistures() {
      return this.$store.getters.growSoilMoistures
    },
  },
  mounted() {
    this.$store.dispatch('listPfObservations')
    this.initSelected()
  },
  watch: {},
  methods: {
    getSoilDescription(soil) {
      const label = 'observation.soilMoistureCondition' + soil
      return i18n.tt(label)
    },
    canCreateNew() {
      const totalCount = this.$store.getters.observations.pf.length
      return totalCount < this.maxObservationCount
    },
    openEditor(observation) {
      this.showingEditor = true
      this.initErrors()
      if (observation) {
        this.selectedObservation = observation
        if (UserInfo.isFSMigrated()) {
          this.selectedFieldId = this.selectedObservation.fireFieldId
        } else {
          this.selectedFieldId = this.selectedObservation.placeId
        }

        this.selectedSoil = this.selectedObservation.soil
      } else {
        this.initSelected()
      }
    },
    closeEditor() {
      this.showingEditor = false
      this.initSelected()
      this.initErrors()
    },
    initSelected() {
      this.selectedObservation = null
      this.selectedFieldId = null
      this.selectedSoil = Soil.SANDY_LOAM
    },
    initErrors() {
      this.fieldSelectError = false
      this.fieldSelectErrorMessage = null
    },
    onSaveSuccess() {
      this.closeEditor()
    },
    onSaveFailure() {
      this.closeEditor()
    },
    createOrUpdate() {
      if (!this.selectedFieldId) {
        this.fieldSelectError = true
        this.fieldSelectErrorMessage = i18n.tt('customErrorFieldRequired')
        return
      }
      let request = {
        observation: {
          placeId: this.selectedFieldId,
          drainage: null,
          soil: this.selectedSoil,
          placeType: 'Field',
        },
      }

      if (UserInfo.isFSMigrated()) {
        request.observation.fireFieldId = this.selectedFieldId
      }

      let action = null
      if (this.selectedObservation) {
        request.id = this.selectedObservation.id
        action = 'updatePfObservation'
      } else {
        action = 'createPfObservation'
      }
      const params = { onSuccess: this.onSaveSuccess, onFailure: this.onSaveFailure, request: request }
      this.$store.dispatch(action, params)
    },

    openDeleteDialog(observationInfo) {
      this.selectedObservation = observationInfo.observation
      const fieldName = observationInfo.field.name
      this.deleteConfirmationMessage = i18n.tt('observation.deleteSoilMoistureLocation', { fieldName: fieldName })
      this.showingDeleteDialog = true
    },
    closeDeleteDialog() {
      this.initSelected()
      this.deleteConfirmationMessage = null
      this.showingDeleteDialog = false
    },

    onDeleteSuccess() {
      this.closeDeleteDialog()
    },
    onDeleteFailure() {
      this.closeDeleteDialog()
    },
    destroy() {
      const params = {
        request: { id: this.selectedObservation.id },
        onSuccess: this.onDeleteSuccess,
        onFailure: this.onDeleteFailure,
      }
      this.$store.dispatch('deletePfObservation', params)
    },
  },
}
</script>
